<!-- EntityRecommendations.vue -->
<template>
  <div>
    <!-- {{ formattedRecommendations }} -->
    <div v-if="loading">
      <p>Loading...</p>
    </div>
    <div v-else-if="!hasDossierContent" class="empty-state">
      <Title size="3" class="empty-state-title">Complete Your Dossier First</Title>
      <Title size="5" class="empty-state-subtitle">Add information to your dossier to get personalized recommendations</Title>
      <Button
        type="primary"
        size="large"
        class="empty-state-button"
        :to="{
          name: 'entity-dossier',
          params: {
            entityExternalId: props.entityExternalId,
            entityType: props.entity.type,
          },
        }"
      >
        Go to Dossier
      </Button>
    </div>
    <div v-else-if="formattedRecommendations?.length">
      <!-- Tabs and regenerate button -->
      <div class="recommendations-header">
        <div class="tabs">
          <button 
            class="tab-button" 
            :class="{ active: activeTab === 'all' }" 
            @click="activeTab = 'all'">
            All ({{ formattedRecommendations.length }})
          </button>
          <!-- <button 
            class="tab-button" 
            :class="{ active: activeTab === 'comparison' }" 
            @click="activeTab = 'comparison'">
            Model Comparison
          </button> -->
        </div>
        <Button
          type="primary"
          size="small"
          :disabled="loading"
          @click="generateObservationsRecommendations"
        >
          Regenerate
        </Button>
      </div>
      
      <!-- Filtered items based on active tab -->
      <!-- Standard view for observations and recommendations -->
      <div v-if="activeTab !== 'comparison'">
        <div 
          v-for="(item, index) in filteredItems" 
          :key="index" 
          class="card recommendation-card"
          >
          <!-- Item type badge -->
          <!-- <div class="item-type-badge" :class="item.type">
            {{ item.type === 'observation' ? 'Observation' : 'Recommendation' }}
          </div>
           -->
          <h3 class="card-headline">{{ item.headline }}</h3>
          <p class="card-detail">{{ item.detail }}</p>
          <p class="card-action"><strong>Action:</strong> {{ item.action }}</p>
          <!-- <div class="button-row">
            <button @click="saveRecommendation(index)">Save</button>
            <button @click="deleteRecommendation(index)">Delete</button>
          </div> -->
        </div>
      </div>
      
      <!-- Simple Model Comparison View - Side by Side Columns -->
    </div>
    <div v-else class="empty-state">
      <Title size="3" class="empty-state-title">No Recommendations Available</Title>
      <Title size="5" class="empty-state-subtitle">Generate recommendations to get started</Title>
      <Button
        type="primary"
        size="large"
        class="empty-state-button"
        :disabled="loading"
        @click="generateObservationsRecommendations"
      >
        Generate Recommendations
      </Button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { awaitTask } from '@frontend/ui/features/checkup/helpers/tasks';
import Button from '@stakeui/form/button/Button.vue';
import Title from '@stakeui/content/text/Title.vue';

const props = defineProps({
  entityExternalId: {
    type: String,
    required: true,
  },
  entity: {
    type: Object,
    required: true,
  },
});

const recommendations = ref(props.entity.latest_recommendations?.basic_recommendations);
const loading = ref(false);
const activeTab = ref('all'); // Default to showing all items
const hasDossierData = ref(false);

const headers = computed(() => ({
  'X-CSRFToken': document.querySelector('[name="csrfmiddlewaretoken"]').value,
  'Content-Type': 'application/json',
}));

const formattedRecommendations = computed(() => {
  // Process the data to ensure each item has required properties
  // Handle array format
  return recommendations.value;
});

// Filter items based on active tab
const filteredItems = computed(() => {
  return formattedRecommendations.value;
});

const hasDossierContent = computed(() => {
  return hasDossierData.value;
});

// Check if entity has dossier content
const checkDossierContent = async () => {
  const response = await fetch(`/api/v1/dossier/entity/${props.entityExternalId}/`);
  if (response.ok) {
    const data = await response.json();
    const dossierData = data?.dossier?.dossier_data ?? {};

    hasDossierData.value =
      !!dossierData && Object.keys(dossierData).length > 0;
  } else {
    hasDossierData.value = false;
  }
};

// Determine if an item is an observation or recommendation
const fetchObservationsRecommendations = async () => {
  loading.value = true;
  try {
    const response = await awaitTask(`/api/v1/entities/${props.entityExternalId}/recommendations/generate/`, {
      headers: headers.value,
    });
    const recData = response;
    console.log('Observations and Recommendations:', recData);
    recommendations.value = recData.task.meta.recommendations?.basic_recommendations;
  } catch (error) {
    console.error('Error fetching observations and recommendations:', error);
  } finally {
    loading.value = false;
  }
};

const generateObservationsRecommendations = () => {
  fetchObservationsRecommendations();
};

const saveRecommendation = index => {
  console.log('Save recommendation:', formattedRecommendations.value[index]);
};

const deleteRecommendation = index => {
  // Implementation for deleting recommendation
};

// Check dossier content when component is mounted
onMounted(() => {
  console.log('EntityRecommendations mounted');
  checkDossierContent();
});
</script>

<style scoped>
.card {
  border: 1px solid #eaeaea;
  padding: 1.25rem;
  margin-bottom: 1rem;
  position: relative;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
  transition: transform 0.2s, box-shadow 0.2s;
}

.card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.observation-card {
  border-left: 4px solid #3498db; /* Blue for observations */
  background-color: #f8fafc;
}

.recommendation-card {
  border-left: 4px solid #27ae60; /* Green for recommendations */
  background-color: #f0f9f4;
}

.item-type-badge {
  position: absolute;
  top: 0;
  right: 0;
  padding: 4px 8px;
  border-radius: 0 4px 0 4px;
  font-size: 12px;
  font-weight: bold;
  color: white;
}

.item-type-badge.observation {
  background-color: #3498db;
}

.item-type-badge.recommendation {
  background-color: #27ae60;
}

.tabs {
  display: flex;
  margin-bottom: 16px;
  border-bottom: 1px solid #ddd;
}

.recommendations-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.tab-button {
  padding: 8px 16px;
  background: none;
  border: none;
  cursor: pointer;
  font-weight: 500;
  color: #666;
}

.tab-button.active {
  border-bottom: 2px solid #3498db;
  color: #3498db;
}

.button-row {
  display: flex;
  gap: 8px;
  margin-top: 8px;
}

/* Empty state styles */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 4rem 2rem;
  min-height: 20rem;
}

.empty-state-title {
  margin-bottom: 1rem;
  color: var(--stk-text);
}

.empty-state-subtitle {
  margin-bottom: 2rem;
  color: var(--stk-text-light);
}

.empty-state-button {
  min-width: 240px;
}

/* Comparison View Styles */
/* Simple Comparison View Styles */
.simple-comparison-view {
  border: 1px solid #eaeaea;
  border-radius: 4px;
  overflow: hidden;
}

.model-columns-container {
  display: flex;
  gap: 1px;
  background-color: #eaeaea;
}

.model-column {
  flex: 1;
  background-color: #fff;
}

.model-header {
  padding: 10px;
  font-weight: bold;
  text-align: center;
  background-color: #f1f5f9;
  border-bottom: 1px solid #eaeaea;
}

.model-section {
  padding: 10px;
  margin-bottom: 10px;
}

.section-header {
  font-weight: bold;
  padding: 5px 0;
  margin-bottom: 10px;
  border-bottom: 1px solid #eaeaea;
}

.model-item {
  padding: 10px;
  margin-bottom: 10px;
  border-radius: 4px;
  position: relative;
}

.recommendation-item {
  background-color: rgba(39, 174, 96, 0.1);
  border-left: 3px solid #27ae60;
}

.observation-item {
  background-color: rgba(52, 152, 219, 0.1);
  border-left: 3px solid #3498db;
}

.item-headline {
  font-weight: bold;
  margin-bottom: 5px;
}

.item-detail {
  font-size: 0.9em;
  color: #666;
}

.empty-message {
  padding: 10px;
  color: #999;
  font-style: italic;
  text-align: center;
}

/* Card Content Styling */
.card-headline {
  font-size: 1.25rem;
  font-weight: 600;
  margin-top: 0;
  margin-bottom: 0.75rem;
  color: var(--stk-text, #333);
}

.card-detail {
  font-size: 0.95rem;
  color: var(--stk-text-light, #666);
  margin-bottom: 1rem;
  line-height: 1.5;
}

.card-action {
  font-size: 0.95rem;
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid #eaeaea;
}
</style>
